import * as React from 'react';
import { FC, useState, useCallback, useRef } from 'react';
import { Alert, Form, Modal, Progress, Row } from 'antd';
import moment from 'moment';
import { compareStrings, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getFileMimeTypeFromString } from '../../utils/FileAccessUtils';
import { parseMessageToElements } from '../../utils/ChatUtils';
import { retrieveCachedUserDetails } from '../../thunks/session_thunks';
import { workOrderChatUploadCustomRequest } from '../../thunks/file_thunks';
import ChatInput from '../ChatInput';
import { ImageThumbnails } from '../image_thumbnails/ImageThumbnails';
import NewNoteDisplay from '../buyer_new_work_order_note_form/NewNoteDisplay';
import { getNewNoteType } from '../../utils/WebSocketUtils';

/**
 * Normalize an upload callback's `data` field to a URL string.
 * workOrderChatUploadCustomRequest returns `data` as a string (cloudFrontUrl).
 * owuploadCustomRequest returns `data` as an object `{ fileUrl, presignedUrl, ... }`.
 */
const extractUploadedUrl = (data: any): string => {
	if (typeof data === 'string') return data;
	return nullSafeGetOrElse('fileUrl', data, '') || nullSafeGetOrElse('cloudFrontUrl', data, '');
};

interface CommonNoteInputFormProps {
	/**
	 * Called with the note entity. Caller binds entity-specific context (entityId, bidder, etc.)
	 * before passing this function.
	 */
	appendNote: (note: any) => Promise<any>;
	currentUser: any;
	typingDetails: any[];
	newNoteCount: number;
	onTyping: () => void;
	onSendNote: (text?: string, noteType?: string) => void;
	onRefresh: () => void;
	/**
	 * Passed to ChatInput for mentions/upload context.
	 * Should have an `id` field used as workOrderId for audio uploads.
	 */
	workOrder?: any;
	/**
	 * Optional. If provided, video call button is shown.
	 * Caller is responsible for computing the meeting room name before
	 * passing this function (no room name arg needed here).
	 */
	getMeetingToken?: () => Promise<string>;
	getOWShortenedLink?: (url: string) => Promise<string>;
	/**
	 * Optional. If provided, called with the note entity before appendNote
	 * (used by supplier/bidder forms to show optimistic loading messages).
	 */
	onLoadingMessage?: (note: any) => void;
	errors?: string[];
	/** Optional custom upload function. When provided, used instead of workOrderChatUploadCustomRequest for file uploads. */
	customUploadRequest?: (args: any) => void;
	/**
	 * Optional custom mention fetcher `(search) => Promise<contacts[]>`. When provided, the chat
	 * input's @-mention autocomplete uses it instead of the default work-order subscriber thunks.
	 * Callers reusing this form for a non-work-order entity (e.g. walkthroughs) must pass this so
	 * mentions hit the correct subscriber endpoint.
	 */
	fetchMentionUsers?: (search: string) => Promise<any>;
	/**
	 * Optional slot rendered directly above the chat input (e.g. the walkthrough task chip the note
	 * is being attached to). Omitted by default so work-order callers render nothing.
	 */
	renderAboveInput?: () => React.ReactNode;
}

const CommonNoteInputForm: FC<CommonNoteInputFormProps> = ({
	appendNote,
	currentUser,
	typingDetails,
	newNoteCount,
	onTyping,
	onSendNote,
	onRefresh,
	workOrder,
	getMeetingToken,
	getOWShortenedLink,
	onLoadingMessage,
	errors = [],
	customUploadRequest,
	fetchMentionUsers,
	renderAboveInput,
}) => {
	const [form] = Form.useForm();
	const token = retrieveCachedUserDetails(['token']).token;

	const [photoFileList, setPhotoFileList] = useState<any[]>([]);
	const [photoLinks, setPhotoLinks] = useState<string[]>([]);
	const [photoPreviewModalVisible, setPhotoPreviewModalVisible] = useState(false);
	const [videoFileList, setVideoFileList] = useState<any[]>([]);
	const [videoLink, setVideoLink] = useState('');
	const [videoPreviewModalVisible, setVideoPreviewModalVisible] = useState(false);
	const [audioFileList, setAudioFileList] = useState<any[]>([]);
	const [fileUploading, setFileUploading] = useState(false);
	const [uploadingFileCount, setUploadingFileCount] = useState(0);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isRecording, setIsRecording] = useState(false);
	const [isRecordingPaused, setIsRecordingPaused] = useState(false);

	const sendNote = useCallback(
		(entity: any) => {
			if (nullSafeGetOrElse('noteType', entity, null) === 'heartbeat') {
				console.log('___ Ignoring append notes for failed sockets for heartbeat ___ ');
				return Promise.resolve();
			}
			return appendNote(entity).then(() => {
				onSendNote(entity.text || undefined, getNewNoteType(entity));
				if (!entity.text) {
					const el = document.getElementById('chatBox');
					if (el) el.scrollTop = el.scrollHeight;
				}
			});
		},
		[appendNote, onSendNote]
	);

	const handleSubmit = useCallback(
		(e?: any) => {
			if (e && typeof e.preventDefault === 'function') e.preventDefault();
			const messageText = form.getFieldValue('noteText');
			if (!messageText || !messageText.trim()) return;

			const { elements, text } = parseMessageToElements(messageText);
			const entity = {
				text,
				noteAddedBy: currentUser.email,
				noteAddedByContact: currentUser,
				noteAddedAt: moment.utc(),
				elements,
			};
			if (onLoadingMessage) {
				onLoadingMessage(entity);
			}
			setTimeout(() => {
				const el = document.getElementById('chatBox');
				if (el) el.scrollTop = el.scrollHeight;
			}, 50);
			sendNote(entity);
			form.resetFields();
		},
		[form, currentUser, onLoadingMessage, sendNote]
	);

	const handlePhotoUploadChange = useCallback((info: any) => {
		setPhotoFileList(info.fileList);
		if (info.fileList.some((file) => compareStrings(file.status, 'uploading'))) {
			const activeFiles = info.fileList.filter(
				(file) => file.status === 'uploading' || file.status === 'done'
			);
			const totalFileCount = activeFiles.length;
			const totalProgress = activeFiles.reduce((sum, file) => {
				const progress = file.status === 'done' ? 100 : nullSafeGetOrElse('percent', file, 0);
				return sum + progress;
			}, 0);
			const averageProgress = totalFileCount > 0 ? Math.round(totalProgress / totalFileCount) : 0;
			setFileUploading(true);
			setUploadingFileCount(totalFileCount);
			setUploadProgress(averageProgress);
			return;
		}
		if (info.fileList.every((file) => compareStrings(file.status, 'done'))) {
			const links = info.fileList.map((file) =>
				extractUploadedUrl(nullSafeGet('response.data', file))
			);
			setFileUploading(false);
			setUploadingFileCount(0);
			setUploadProgress(0);
			setPhotoLinks(links || []);
			setPhotoPreviewModalVisible(true);
		}
	}, []);

	const handleVideoUploadChange = useCallback((info: any) => {
		setVideoFileList(info.fileList);
		if (info.file.status === 'uploading') {
			const progress = nullSafeGetOrElse('file.percent', info, 0);
			setFileUploading(true);
			setUploadingFileCount(1);
			setUploadProgress(Math.round(progress));
			return;
		}
		if (info.file.status === 'done') {
			const link = extractUploadedUrl(nullSafeGet('file.response.data', info));
			setFileUploading(false);
			setUploadingFileCount(0);
			setUploadProgress(0);
			setVideoLink(link);
			setVideoPreviewModalVisible(true);
		}
	}, []);

	const onPhotoSubmit = useCallback(() => {
		const entities = photoLinks.map((photoLink) => ({
			text: '',
			photo: photoLink,
			noteAddedBy: currentUser.email,
			noteAddedByContact: currentUser,
			noteAddedAt: moment.utc(),
		}));
		entities.forEach((entity) => sendNote(entity));
		setPhotoLinks([]);
		setPhotoPreviewModalVisible(false);
		setPhotoFileList([]);
	}, [photoLinks, currentUser, sendNote]);

	const onPhotoCancel = useCallback(() => {
		setPhotoLinks([]);
		setPhotoPreviewModalVisible(false);
		setPhotoFileList([]);
	}, []);

	const onVideoSubmit = useCallback(() => {
		const entity = {
			text: '',
			video: videoLink,
			noteAddedBy: currentUser.email,
			noteAddedByContact: currentUser,
			noteAddedAt: moment.utc(),
		};
		sendNote(entity);
		setVideoLink('');
		setVideoPreviewModalVisible(false);
		setVideoFileList([]);
	}, [videoLink, currentUser, sendNote]);

	const onVideoCancel = useCallback(() => {
		setVideoLink('');
		setVideoPreviewModalVisible(false);
		setVideoFileList([]);
	}, []);

	const handleRecordingStateChange = useCallback((recording: boolean, paused: boolean) => {
		setIsRecording(recording);
		setIsRecordingPaused(paused);
	}, []);

	const handleSendAudio = useCallback(
		(_blob: Blob, file: File) => {
			const uploadWorkOrderId = nullSafeGetOrElse('id', workOrder, null);
			const userType = currentUser.userType;

			const uploadFile = {
				uid: Date.now().toString(),
				name: file.name,
				status: 'uploading',
				percent: 0,
			};
			setAudioFileList([uploadFile]);
			setFileUploading(true);
			setUploadingFileCount(1);
			setUploadProgress(0);

			const uploadFn = customUploadRequest || workOrderChatUploadCustomRequest;
			const onProgress = ({ percent }) => {
				setUploadProgress(percent);
				setAudioFileList([{ ...uploadFile, percent }]);
			};
			const onSuccess = ({ data: audioLink }) => {
				setFileUploading(false);
				setUploadingFileCount(0);
				setUploadProgress(0);
				setAudioFileList([]);
				const entity = {
					text: '',
					audio: extractUploadedUrl(audioLink),
					noteAddedBy: currentUser.email,
					noteAddedAt: moment.utc(),
					noteAddedByContact: currentUser,
				};
				sendNote(entity);
			};
			const onError = () => {
				setFileUploading(false);
				setUploadingFileCount(0);
				setUploadProgress(0);
				setAudioFileList([]);
			};
			uploadFn({
				userType,
				workOrderId: uploadWorkOrderId,
				file,
				onProgress,
				onSuccess,
				onError,
				source: 'VoiceNoteRecorder',
			});
		},
		[workOrder, currentUser, sendNote, customUploadRequest]
	);

	const handleStartVideoCall = useCallback(() => {
		if (!getMeetingToken || !getOWShortenedLink) return;
		getMeetingToken().then((meetingId) => {
			const longUrl = `https://live.useopenwrench.com/?meetingid=${meetingId}`;
			getOWShortenedLink(longUrl).then((url) => {
				const entity = {
					text: `${currentUser.nameGiven} just went live. Join the video call with them at ${url}`,
					noteAddedBy: currentUser.email,
					noteAddedAt: moment.utc(),
					noteAddedByContact: currentUser,
				};
				sendNote(entity);
			});
		});
	}, [getMeetingToken, getOWShortenedLink, currentUser, sendNote]);

	const multiplePhotos = photoLinks.length > 1;

	return (
		<Form layout="inline" onFinish={handleSubmit} form={form}>
			{errors.length > 0
				? errors.map((error, idx) => (
						<Form.Item key={idx}>
							<Alert message={error} type="error" />
						</Form.Item>
				  ))
				: null}
			<Modal
				visible={photoPreviewModalVisible}
				width={600}
				title={multiplePhotos ? 'Send these photos?' : 'Send this photo?'}
				okText="Send"
				onCancel={onPhotoCancel}
				onOk={onPhotoSubmit}
				closable={false}
			>
				<Row style={{ gap: 12 }}>
					{photoLinks.map((link, idx) => (
						<ImageThumbnails key={idx} imageWidth={100} imageHeight={100} photoStrings={[link]} />
					))}
				</Row>
			</Modal>
			<Modal
				visible={videoPreviewModalVisible}
				width={600}
				title="Send this video?"
				okText="Send"
				onCancel={onVideoCancel}
				onOk={onVideoSubmit}
				closable={false}
			>
				<video width="100%" height="auto" controls>
					<source src={videoLink} type={getFileMimeTypeFromString(videoLink)} />
					Your browser does not support the video tag.
				</video>
			</Modal>
			<NewNoteDisplay onRefresh={onRefresh} newNoteCount={newNoteCount} />
			{(fileUploading || isRecording) && (
				<div className="bodyregular mx-auto flex w-1/3 items-center justify-between self-center rounded-3xl rounded-b-none bg-owgray px-4 py-2">
					<span>
						{isRecording
							? isRecordingPaused
								? 'Recording paused'
								: 'Recording an audio clip'
							: `Uploading ${uploadingFileCount} ${uploadingFileCount === 1 ? 'file' : 'files'}`}
					</span>
					{fileUploading ? <Progress type="circle" percent={uploadProgress} width={30} /> : null}
				</div>
			)}
			<div className="relative w-full">
				<Form.Item name="noteText" initialValue="" rules={[]}>
					<ChatInput
						handlePhotoUploadChange={handlePhotoUploadChange}
						handleStartVideoCall={getMeetingToken ? handleStartVideoCall : undefined}
						workOrder={workOrder}
						fetchMentionUsers={fetchMentionUsers}
						handleVideoUploadChange={handleVideoUploadChange}
						handleSubmit={handleSubmit}
						currentUser={currentUser}
						token={token}
						photoFileList={photoFileList}
						videoFileList={videoFileList}
						onTyping={onTyping}
						typingDetails={typingDetails}
						onSendAudio={handleSendAudio}
						onRecordingStateChange={handleRecordingStateChange}
						customUploadRequest={customUploadRequest}
						renderAboveInput={renderAboveInput}
					/>
				</Form.Item>
			</div>
		</Form>
	);
};

export default CommonNoteInputForm;
